import { useCallback, useMemo } from 'react';

import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { Outlet } from 'react-router';

import { dateTime, useBoolean } from '@/_shared';

import { useBridgePlanContext } from '../bridge-plan';

import { AuditTrailTemplate } from './audit-trail-template';
import { AuditTrailContext } from './context';

export function AuditTrailProvider() {
  const loading = useBoolean();
  const bridgePlan = useBridgePlanContext();

  const getAuditTrailFile = useCallback(async () => {
    loading[1].setTrue();
    const {
      leadInfo: { email: lEmail, mobilePhone, leadName },
      order_data: { auditLog: auditLogData },
    } = bridgePlan;
    const { representative, customer, termsSubSections } = auditLogData;

    const dateFormat = 'MMM D, YYYY, hh:mm:ss';
    const nowDate = dateTime.utcDateString(dateTime.now(true));
    const generationDate = dateTime.format(dateTime.utcDateString(nowDate), dateFormat);
    const tncPage1SignedAt = dateTime.format(dateTime.utcDateString(termsSubSections[0].signedDate), dateFormat);
    const tncPage2SignedAt = dateTime.format(dateTime.utcDateString(termsSubSections[1].signedDate), dateFormat);
    const tncPage3SignedAt = dateTime.format(dateTime.utcDateString(termsSubSections[2].signedDate), dateFormat);
    const tncPage4SignedAt = dateTime.format(dateTime.utcDateString(termsSubSections[3].signedDate), dateFormat);

    const doc = (
      <AuditTrailTemplate
        generationDate={generationDate}
        email={lEmail}
        phone={mobilePhone}
        tncSignedBy={leadName}
        signeeIpAddress={customer?.ipAddress ?? representative?.ipAddress ?? ''}
        tncPage1SignedAt={tncPage1SignedAt}
        tncPage2SignedAt={tncPage2SignedAt}
        tncPage3SignedAt={tncPage3SignedAt}
        tncPage4SignedAt={tncPage4SignedAt}
      />
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const asPdf = pdf([] as any);
    asPdf.updateContainer(doc);

    const blob = await asPdf.toBlob();
    await saveAs(blob, `audit-trail-${dateTime.format(nowDate, 'DD-MM-YYYY-hh-mm-ss')}.pdf`);
    loading[1].setFalse();
  }, [bridgePlan, loading]);

  const value = useMemo(
    () => ({
      getAuditTrailFile,
      loading: loading[0],
      disabled: bridgePlan.isLoading,
      hasAuditTrail: !!bridgePlan.order_data?.auditLog,
    }),
    [getAuditTrailFile, loading, bridgePlan.isLoading, bridgePlan.order_data?.auditLog]
  );

  return (
    <AuditTrailContext.Provider value={value}>
      <Outlet />
    </AuditTrailContext.Provider>
  );
}
